<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  tags?: Array<string | number | null | undefined>
  emptyLabel?: string
  removable?: boolean
}>(), {
  tags: () => [],
  emptyLabel: '',
  removable: false,
})

const normalizedTags = computed(() => {
  return (props.tags || [])
    .map(tag => tag?.toString()?.trim())
    .filter((tag): tag is string => Boolean(tag))
})

const emit = defineEmits<{
  remove: [tag: string]
}>()

const onRemove = (tag: string) => {
  emit('remove', tag)
}
</script>

<template>
  <div class="tag-list">
    <template v-if="normalizedTags.length">
      <span
        v-for="tag in normalizedTags"
        :key="tag"
        class="tag-pill"
      >
        <span>{{ tag }}</span>
        <button
          v-if="removable"
          type="button"
          class="tag-remove"
          aria-label="Remove tag"
          @click="onRemove(tag)"
        >
          ×
        </button>
      </span>
    </template>
    <span
      v-else-if="emptyLabel"
      class="tag-empty"
    >
      {{ emptyLabel }}
    </span>
  </div>
</template>

<style scoped>
.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 9999px;
  background: var(--theme-surface-interactive);
  color: var(--theme-on-surface);
  border: 1px solid var(--theme-border);
  font-size: 0.85rem;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

.tag-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
}

.tag-remove:hover {
  background: var(--theme-surface-interactive-hover);
}

.tag-empty {
  color: var(--theme-text-secondary);
  font-size: 0.9rem;
}
</style>
